export const superAdmin = {
  common: {
    actions: 'Actions',
  },
  index: {
    title: 'Super-Admin Dashboard',
  },
  organizations: {
    active: 'Active',
    createdAt: 'Created At',
    create: 'Create Organization',
    deleteDialog: {
      confirm: 'Deactivate Organization',
      deleting: 'Deactivating...',
      description:
        'The organization will be marked as inactive and will not be accessible to users.',
      title: 'Deactivate Organization',
    },
    description: 'Manage and organize your platform organizations',
    form: {
      create: {
        description: 'Fill in the details to create a new organization.',
        submit: 'Create Organization',
        submitting: 'Creating...',
        title: 'Create New Organization',
      },
      edit: {
        description: 'Update the organization details.',
        submit: 'Update Organization',
        submitting: 'Updating...',
        title: 'Edit Organization',
      },
      fields: {
        logo: {
          help: 'URL to the organization logo image',
          label: 'Logo URL',
          placeholder: 'https://example.com/logo.png',
        },
        name: {
          label: 'Organization Name',
          placeholder: 'Acme Inc',
          required: 'Organization name is required',
        },
        slug: {
          help: 'A unique identifier for the organization',
          label: 'Slug',
          placeholder: 'acme-inc',
          required: 'Slug is required',
        },
      },
    },
    inactive: 'Inactive',
    member: 'Member',
    members: 'Members',
    name: 'Organization Name',
    organizationDetails: 'Organization Details',
    restoreDialog: {
      confirm: 'Reactivate Organization',
      description:
        'The organization will be marked as active and will be accessible to users again.',
      restoring: 'Reactivating...',
      title: 'Reactivate Organization',
    },
    slug: 'Slug',
    status: 'Status',
    title: 'Organizations Management',
  },
  tabs: {
    organizations: 'Organizations',
    users: 'Users',
    demos: 'Demos',
  },
  demos: {
    title: 'Demos Management',
    description: 'Pre-generate personalized demos for sales prospects',
    create: 'Create Demo',
    loading: 'Loading demos...',
    loadError: 'Failed to load demos. Please try again.',
    empty: {
      title: 'No demos yet',
      description:
        'Create your first personalized demo to showcase Referent to a prospect.',
    },
    overallStatus: {
      pending: 'Pending',
      generating: 'Generating',
      ready: 'Ready',
      partial: 'Partial',
      failed: 'Failed',
    },
    artifactStatus: {
      pending: 'Pending',
      generating: 'Generating',
      completed: 'Completed',
      failed: 'Failed',
    },
    artifacts: {
      audit: 'Audit',
      competitors: 'Competitors',
      companyContext: 'Company context',
      brandVoice: 'Brand Voice',
      knowledgeBase: 'Knowledge Base',
      keywordGap: 'Keyword Gap',
      keywordResearch: 'Keyword Research',
      contentPlan: 'Content Plan',
      contentArticle: 'Article',
      deliverables: 'Deliverables',
    },
    card: {
      sourceAuditLabel: 'Source audit',
      createdByLabel: 'Created by',
      readyAtLabel: 'Ready at',
      costLabel: 'AI spend',
      emailLabel: 'Demo email',
      copyEmail: 'Copy email',
      emailCopied: 'Email copied to clipboard',
      emailCopyError: 'Failed to copy email',
      openAsDemoLabel: 'Open as demo',
      openAsDemoDialogTitle: 'Open as the demo user?',
      openAsDemoDialogDescription:
        "You'll be signed in as this demo's user and leave the admin panel. Use the impersonation banner to return.",
      openAsDemoConfirm: 'Open as demo',
      openAsDemoCancel: 'Cancel',
    },
    actions: {
      label: 'Actions',
      regenerate: 'Regenerate artifact...',
      rotateCredentials: 'Rotate credentials',
      editSalesNote: 'Edit sales note',
      deleteDemo: 'Delete demo',
    },
    form: {
      title: 'Create New Demo',
      description:
        "Pick a completed audit for your prospect's URL and we'll seed an org with cloned artifacts.",
      submit: 'Create Demo',
      submitting: 'Creating demo...',
      fields: {
        companyName: {
          label: 'Company name',
          placeholder: "Prospect's company name",
          help: "Used to label the demo org. Visible to the prospect after login.",
        },
        auditId: {
          label: 'Source audit',
          placeholder: 'Select a completed audit',
          search: 'Search by URL...',
          empty: "No completed audits with confirmed competitors. Run an SEO audit on your prospect's URL first.",
          loading: 'Loading audits...',
          competitors: '{{count}} competitors',
          competitorsOne: '{{count}} competitor',
        },
        salesNote: {
          label: 'Sales note',
          placeholder: 'Optional context about the prospect for your future self.',
          help: 'Stored on the demo session. Max 2000 characters.',
        },
        forceCreate: {
          label: 'Create anyway',
          description:
            'An active demo already exists for this audit. Check this to create another anyway.',
        },
      },
      errors: {
        companyNameRequired: 'Company name is required',
        companyNameTooShort: 'Company name must be at least 2 characters',
        companyNameTooLong: 'Company name must be at most 120 characters',
        auditIdRequired: 'Select a source audit',
        salesNoteTooLong: 'Sales note must be at most 2000 characters',
      },
    },
    credentialsModal: {
      title: 'Demo credentials',
      warning: 'These credentials are shown only once. Copy them now before closing.',
      costWarning:
        "You're approaching your daily AI-spend limit ($20+ in the last 24h).",
      emailLabel: 'Email',
      passwordLabel: 'Password',
      loginUrlLabel: 'Login URL',
      copyEmail: 'Copy email',
      copyPassword: 'Copy password',
      copyLoginUrl: 'Copy login URL',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      copied: 'Copied to clipboard',
      copyError: 'Failed to copy to clipboard',
      acknowledge: "I've copied the credentials",
      closeWithoutCopy: 'Close without copying',
      unsavedConfirmation:
        "You haven't copied all credentials. Close anyway?",
    },
    deleteDialog: {
      title: 'Delete demo',
      description:
        'The demo will be soft-deleted and removed from this list. This cannot be undone from the UI.',
      confirm: 'Delete demo',
      cancel: 'Cancel',
    },
    rotateDialog: {
      title: 'Rotate credentials',
      description:
        'A new password will be generated and shown once. The old password will no longer work.',
      confirm: 'Rotate credentials',
      cancel: 'Cancel',
    },
    salesNoteDialog: {
      title: 'Edit sales note',
      description:
        'Internal note about the prospect. Visible only to super-admins. Max 2000 characters.',
      placeholder: 'Add a note about the prospect...',
      save: 'Save note',
      cancel: 'Cancel',
    },
    regenerateDialog: {
      title: 'Regenerate artifact',
      description:
        'Pick which AI-generated artifact to re-run. The audit and competitors cannot be regenerated.',
      artifactLabel: 'Artifact',
      confirm: 'Regenerate',
      cancel: 'Cancel',
    },
    toasts: {
      created: 'Demo created successfully',
      createError: 'Failed to create demo',
      deleted: 'Demo deleted',
      deleteError: 'Failed to delete demo',
      regenerateStarted: 'Regeneration started',
      regenerateError: 'Failed to start regeneration',
      credentialsRotated: 'Credentials rotated',
      credentialsRotateError: 'Failed to rotate credentials',
      salesNoteSaved: 'Sales note saved',
      salesNoteError: 'Failed to save sales note',
    },
    errorCodes: {
      DEMO_AUDIT_NOT_FOUND:
        "The selected audit no longer exists. Pick a different audit.",
      DEMO_AUDIT_NOT_COMPLETED: "The selected audit isn't completed yet.",
      DEMO_AUDIT_NO_COMPETITORS:
        "The selected audit has no confirmed competitors. Run competitor discovery first.",
      DEMO_DUPLICATE_AUDIT:
        'An active demo already exists for this audit. Check "Create anyway" to override.',
      DEMO_DAILY_COST_LIMIT_REACHED:
        "You've hit your daily AI-spend cap. Try again tomorrow.",
      DEMO_LIFETIME_COST_LIMIT_REACHED:
        "This demo has hit its $5 lifetime cost cap. Create a new demo instead.",
      DEMO_ARTIFACT_NOT_REGENERATABLE:
        "This artifact can't be regenerated.",
      DEMO_ARTIFACT_BUSY:
        'This artifact is already regenerating. Wait for it to finish.',
      DEMO_ARTIFACT_PREREQUISITE_MISSING:
        'A prerequisite artifact has not been completed yet. Re-run it first.',
      DEMO_NOT_FOUND: 'Demo not found.',
      DEMO_PROVISION_FAILED:
        'Failed to provision the demo. Please try again or check the server logs.',
      generic: 'Something went wrong. Please try again.',
    },
    filters: {
      searchPlaceholder: 'Search by company or audit URL...',
      statusLabel: 'Status',
      statusAll: 'All statuses',
    },
    pagination: {
      loadMore: 'Load more',
      loading: 'Loading...',
      showing: 'Showing {{count}} of {{total}}',
    },
  },
  orgSwitcher: {
    label: 'Switch Organization',
    placeholder: 'Search organization',
    noOrganization: 'No organization selected',
    noResults: 'No results',
    openInNewTab: 'Open in new tab',
  },
  toasts: {
    impersonationError: 'Failed to start impersonation',
    impersonationStarted: 'Impersonation started successfully',
    impersonationStopped: 'Impersonation stopped successfully',
    memberCreated: 'User added to organization successfully',
    memberCreateError: 'Error adding user to organization',
    memberRemoved: 'User removed from organization',
    memberRemoveError: 'Error removing user from organization',
    memberRoleUpdated: 'Role updated',
    memberRoleUpdateError: 'Error updating role',
    organizationCreateError: 'Error creating organization',
    organizationCreated: 'Organization created successfully',
    organizationDeleteError: 'Error deleting organization',
    organizationDeleted: 'Organization deleted successfully',
    organizationRestoreError: 'Error restoring organization',
    organizationRestored: 'Organization restored successfully',
    organizationUpdateError: 'Error updating organization',
    organizationUpdated: 'Organization updated successfully',
    passwordCopied: 'Password copied',
    passwordCopyError: 'Copy failed',
    passwordCopyErrorDescription: 'Failed to copy password to clipboard',
    passwordCopySuccess: 'Password has been copied to clipboard',
    organizationSwitchError: 'Failed to switch organization',
    stopImpersonationError: 'Failed to stop impersonation',
    userCreateError: 'Error creating user',
    userCreated: 'User created successfully',
    userDeleteError: 'Error deleting user',
    userDeleted: 'User deleted successfully',
    userRestoreError: 'Error restoring user',
    userRestored: 'User restored successfully',
    userUpdateError: 'Error updating user',
    userUpdated: 'User updated successfully',
  },
  impersonation: {
    bannerText: 'You are impersonating {{name}} ({{email}})',
    stopImpersonating: 'Stop Impersonating',
    minimize: 'Minimize banner',
    expand: 'Expand impersonation banner',
  },
  users: {
    active: 'Active',
    createdAt: 'Created At',
    create: 'Create User',
    impersonate: 'Impersonate User',
    deleteDialog: {
      confirm: 'Deactivate User',
      deleting: 'Deactivating...',
      description:
        'The user will be marked as inactive and will not be able to access the platform.',
      title: 'Deactivate User',
    },
    description: 'Manage and monitor platform users and their organization memberships',
    form: {
      create: {
        description: 'Fill in the details to create a new user.',
        submit: 'Create User',
        submitting: 'Creating...',
        title: 'Create New User',
      },
      edit: {
        description: 'Update the user details.',
        submit: 'Update User',
        submitting: 'Updating...',
        title: 'Edit User',
      },
      fields: {
        lastName: {
          label: 'Last Name',
          placeholder: 'Doe',
          required: 'Last name is required',
        },
        linkedin: {
          label: 'LinkedIn URL',
          placeholder: 'https://linkedin.com/in/johndoe',
        },
        name: {
          label: 'First Name',
          placeholder: 'John',
          required: 'First name is required',
        },
        organization: {
          label: 'Organization',
          placeholder: 'Select an organization',
          required: 'Organization is required',
        },
        position: {
          label: 'Position',
          placeholder: 'Software Engineer',
        },
        timezone: {
          label: 'Timezone',
        },
      },
      passwordInfo:
        'The password is generated automatically. To create the new user, it is necessary to copy it.',
      passwordInfoEdit:
        'Leave empty to keep current password, click the regenerate button to generate a new password. After modifying the password, you must copy it to update the user.',
    },
    inactive: 'Inactive',
    memberships: {
      title: 'Organizations',
      description: 'Changes to organizations are applied immediately.',
      deactivated: 'Deactivated',
      empty: 'This user does not belong to any organization',
      lastOrganizationHint: 'A user must belong to at least one organization.',
      roleLabel: 'Role in {{organization}}',
      add: {
        title: 'Add organization',
        organizationPlaceholder: 'Select an organization',
        noOptions: 'No organizations available',
        loading: 'Loading organizations...',
        roleLabel: 'Role for the new organization',
        submit: 'Add',
      },
      remove: {
        label: 'Remove from {{organization}}',
        confirmTitle: 'Remove from {{organization}}?',
        confirmDescription:
          'The user will lose access to {{organization}} and all their active sessions will be closed. They will need to sign in again.',
        confirm: 'Remove',
      },
      errors: {
        alreadyMember: 'The user is already a member of this organization.',
        lastOrganization: 'The user cannot be removed from their last organization.',
        organizationInactive: 'This organization is deactivated. Users cannot be added to it.',
        organizationNotFound: 'The organization does not exist.',
        memberNotFound: 'This membership no longer exists. Refresh and try again.',
        validation: 'The request is invalid. Check the selected organization and role.',
      },
    },
    name: 'User Name',
    noOrganization: 'No Organization',
    organization: 'Organization',
    organizations: 'Organizations',
    restoreDialog: {
      confirm: 'Reactivate User',
      description:
        'The user will be marked as active and will be able to access the platform again.',
      restoring: 'Reactivating...',
      title: 'Reactivate User',
    },
    role: 'Role',
    status: 'Status',
    title: 'Users Management',
  },
} as const
